import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { API_BASE_URL } from '../../config/api';
import './MobileInquiriesHubPage.css';

const FILTERS = ['All', 'Pending', 'Replied'];

const Balloon = () => (
  <svg className="mih-balloon" viewBox="0 0 120 100" fill="none" aria-hidden>
    <ellipse cx="60" cy="88" rx="28" ry="5" fill="currentColor" opacity="0.2" />
    <path d="M60 18 C35 18 25 38 25 52 C25 68 40 78 60 78 C80 78 95 68 95 52 C95 38 85 18 60 18Z" fill="currentColor" opacity="0.25" stroke="currentColor" strokeWidth="1.5" />
    <rect x="48" y="76" width="24" height="14" rx="2" fill="currentColor" opacity="0.4" />
    <circle cx="55" cy="58" r="3" fill="#fff" />
    <path d="M58 58 L68 52" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const MobileInquiriesHubPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('All');
  const [filterOpen, setFilterOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch] = useState('');

  const fetchInquiries = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(`${API_BASE_URL}/messages/inquiries/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error('Failed to load');
      const data = await resp.json();
      setInquiries(data.inquiries || []);
    } catch (e) {
      setError(e.message || 'Error');
      setInquiries([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchInquiries();
  }, [fetchInquiries]);

  const formatTime = (d) => {
    if (!d) return '';
    const diff = Date.now() - new Date(d).getTime();
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return new Date(d).toLocaleDateString();
  };

  const filtered = useMemo(() => {
    let list = inquiries;
    if (filter === 'Pending') list = list.filter((q) => (q.messageCount || 0) <= 1);
    if (filter === 'Replied') list = list.filter((q) => (q.messageCount || 0) > 1);
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((inq) => {
        const name = (inq.otherUser?.name || '').toLowerCase();
        const items = (inq.inquiryItems || []).map((i) => (i.name || i.description || i.productName || '').toLowerCase()).join(' ');
        return name.includes(q) || items.includes(q);
      });
    }
    return list;
  }, [inquiries, filter, search]);

  const openInquiry = (inq) => {
    navigate(`/messages?chatId=${inq.id}`);
  };

  return (
    <div className="mih-page">
      <header className="mih-header">
        <button type="button" className="mih-back" onClick={() => navigate(-1)} aria-label="Back">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <h1 className="mih-title">Inquiries</h1>
      </header>

      <div className="mih-toolbar">
        <div className="mih-filter-wrap">
          <button type="button" className="mih-filter" onClick={() => setFilterOpen((o) => !o)} aria-expanded={filterOpen}>
            {filter}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
          {filterOpen && (
            <div className="mih-dropdown" role="listbox">
              {FILTERS.map((f) => (
                <button
                  key={f}
                  type="button"
                  role="option"
                  className={f === filter ? 'is-active' : ''}
                  onClick={() => {
                    setFilter(f);
                    setFilterOpen(false);
                  }}
                >
                  {f}
                </button>
              ))}
            </div>
          )}
        </div>
        {searchOpen ? (
          <input
            className="mih-search-field"
            placeholder="Search inquiries…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search inquiries"
            autoFocus
          />
        ) : null}
        <button
          type="button"
          className="mih-search-btn"
          aria-label={searchOpen ? 'Close search' : 'Search'}
          onClick={() => {
            setSearchOpen((o) => !o);
            if (searchOpen) setSearch('');
          }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {searchOpen ? (
              <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" />
            ) : (
              <g>
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </g>
            )}
          </svg>
        </button>
      </div>

      <div className="mih-body">
        {loading ? (
          <div className="mih-loading">Loading…</div>
        ) : error ? (
          <div className="mih-loading">
            <p>{error}</p>
            <button type="button" className="mih-retry" onClick={fetchInquiries}>Retry</button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="mih-empty">
            <Balloon />
            <p className="mih-empty-text">No content</p>
          </div>
        ) : (
          filtered.map((inq) => {
            const replied = (inq.messageCount || 0) > 1;
            return (
              <button type="button" key={inq.id} className="mih-card" onClick={() => openInquiry(inq)}>
                <div className="mih-card-top">
                  <p className="mih-card-name">{inq.otherUser?.name || 'Supplier'}</p>
                  <span className="mih-card-time">{formatTime(inq.updatedAt)}</span>
                </div>
                {inq.inquiryItems?.length > 0 && (
                  <p className="mih-card-meta">
                    {inq.inquiryItems.length} product{inq.inquiryItems.length > 1 ? 's' : ''} inquired
                  </p>
                )}
                <span className={`mih-badge ${replied ? 'mih-badge--replied' : 'mih-badge--pending'}`}>
                  {replied ? 'Replied' : 'Pending'}
                </span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};

export default MobileInquiriesHubPage;
